import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";

import { useSearchParams } from "react-router-dom";

export type FilterStorage = "url" | "local";

export interface FilterContextValue {
  filterState: Record<string, string>;
  hasAnyActive: boolean;
  updateFilters: (updates: Record<string, string | undefined>) => void;
  setFilter: (queryKey: string, value: string) => void;
  removeFilter: (queryKey: string) => void;
  clearAllFilters: () => void;
}

const FilterContext = createContext<FilterContextValue | null>(null);

const applyFilterUpdates = (
  prev: Record<string, string>,
  updates: Record<string, string | undefined>
): Record<string, string> => {
  const next = { ...prev };
  Object.entries(updates).forEach(([key, value]) => {
    if (value !== undefined) {
      next[key] = value;
    } else {
      delete next[key];
    }
  });
  return next;
};

const useFilterContextValue = (
  filterState: Record<string, string>,
  rawUpdateFilters: (updates: Record<string, string | undefined>) => void,
  filterKeys: string[]
): FilterContextValue => {
  const hasAnyActive = Object.values(filterState).some((v) => v !== "");

  // A saved view can carry a key for a filter that isn't registered here, e.g. bai_codes
  // selected by a business user and later applied by a consumer user who can't see that
  // filter. Drop it rather than storing a value no chip can display or clear.
  const updateFilters = useCallback(
    (updates: Record<string, string | undefined>) => {
      const allowedKeys = new Set(filterKeys);
      const sanitized = Object.fromEntries(
        Object.entries(updates).filter(([key]) => allowedKeys.has(key))
      );
      if (Object.keys(sanitized).length > 0) {
        rawUpdateFilters(sanitized);
      }
    },
    [rawUpdateFilters, filterKeys]
  );

  const setFilter = useCallback(
    (queryKey: string, value: string) => {
      updateFilters({ [queryKey]: value });
    },
    [updateFilters]
  );

  const removeFilter = useCallback(
    (queryKey: string) => {
      updateFilters({ [queryKey]: undefined });
    },
    [updateFilters]
  );

  const clearAllFilters = useCallback(() => {
    const updates: Record<string, undefined> = {};
    filterKeys.forEach((key) => {
      updates[key] = undefined;
    });
    updateFilters(updates);
  }, [filterKeys, updateFilters]);

  return useMemo<FilterContextValue>(
    () => ({
      filterState,
      hasAnyActive,
      updateFilters,
      setFilter,
      removeFilter,
      clearAllFilters,
    }),
    [
      filterState,
      hasAnyActive,
      updateFilters,
      setFilter,
      removeFilter,
      clearAllFilters,
    ]
  );
};

type FilterProviderContentProps = {
  filterKeys: string[];
  children: ReactNode;
};

const UrlFilterProvider = ({
  filterKeys,
  children,
}: FilterProviderContentProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterState, setFilterState] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    filterKeys.forEach((key) => {
      const value = searchParams.get(key);
      if (value != null) {
        initial[key] = value;
      }
    });
    return initial;
  });

  const internalUpdateRef = useRef(false);

  const updateFilters = useCallback(
    (updates: Record<string, string | undefined>) => {
      setFilterState((prev) => applyFilterUpdates(prev, updates));

      internalUpdateRef.current = true;
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          Object.entries(updates).forEach(([key, value]) => {
            if (value !== undefined) {
              next.set(key, value);
            } else {
              next.delete(key);
            }
          });
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  useEffect(() => {
    if (internalUpdateRef.current) {
      internalUpdateRef.current = false;
      return;
    }

    const fromUrl: Record<string, string> = {};
    filterKeys.forEach((key) => {
      const value = searchParams.get(key);
      if (value != null) {
        fromUrl[key] = value;
      }
    });
    setFilterState(fromUrl);
  }, [searchParams, filterKeys]);

  const value = useFilterContextValue(filterState, updateFilters, filterKeys);

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
};

const LocalFilterProvider = ({
  filterKeys,
  children,
}: FilterProviderContentProps) => {
  const [filterState, setFilterState] = useState<Record<string, string>>({});

  const updateFilters = useCallback(
    (updates: Record<string, string | undefined>) => {
      setFilterState((prev) => applyFilterUpdates(prev, updates));
    },
    []
  );

  useEffect(() => {
    setFilterState((prev) => {
      const next: Record<string, string> = {};
      filterKeys.forEach((key) => {
        if (prev[key] !== undefined) {
          next[key] = prev[key];
        }
      });
      return Object.keys(next).length === Object.keys(prev).length
        ? prev
        : next;
    });
  }, [filterKeys]);

  const value = useFilterContextValue(filterState, updateFilters, filterKeys);

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
};

export const FilterProvider = ({
  filterKeys,
  storage = "url",
  children,
}: FilterProviderContentProps & { storage?: FilterStorage }) => {
  const Provider =
    storage === "local" ? LocalFilterProvider : UrlFilterProvider;
  return <Provider filterKeys={filterKeys}>{children}</Provider>;
};

export const useFilterContext = (): FilterContextValue => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error("useFilterContext must be used within a FilterProvider");
  }
  return context;
};
